import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Checkbox, SkeletonPlaceholder } from 'carbon-components-react';
import { isMobileOnly } from 'react-device-detect';
import { SelectedPage } from 'types';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.js`;

interface PDFPreviewProps {
  fileId: number;
  onPageSelection: (pageNumbers: number[], isAllChecked: boolean) => void;
  setSelectedPage: React.Dispatch<React.SetStateAction<SelectedPage | null>>;
  pdfUrl: string;
  selectedFileId: string;
  pageNumbers?: number[];
}

const PDFPreview: React.FC<PDFPreviewProps> = ({
  fileId,
  onPageSelection,
  setSelectedPage,
  pdfUrl,
  selectedFileId,
  pageNumbers
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [numPages, setNumPages] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isPasswordError, setIsPasswordError] = useState<boolean>(false);
  const pageRef = useRef<any>(null);

  const onDocumentPasswordRequest = useCallback(
    (callback: (password: string) => void) => {
      // Immediately reject any password request
      setHasError(true);
      setIsPasswordError(true);
      setIsLoading(false);
      console.error('PDF is password protected', JSON.stringify(callback));
    },
    []
  );

  // Reset states when pdfUrl changes
  useEffect(() => {
    if (pdfUrl) {
      setIsLoading(true);
      setHasError(false);
      setIsPasswordError(false);
    }
  }, [pdfUrl]);

  const onDocumentLoadSuccess = useCallback((pdf: any) => {
    pageRef.current = pdf;
    setNumPages(pdf.numPages);
    setHasError(false);
    setIsPasswordError(false);
    setIsLoading(false);
  }, []);

  const handlePageClick = useCallback(
    (pageNum: number, fileId: number) => async () => {
      if (pageRef.current) {
        try {
          const page = await pageRef.current.getPage(pageNum);
          const scale = 2;
          const viewport = page.getViewport({ scale });

          const isPortrait = viewport.height > viewport.width;
          const isLandscape = viewport.width > viewport.height;
          const isSquare = viewport.width === viewport.height;

          const tempCanvas = document.createElement('canvas');
          const context = tempCanvas.getContext('2d');

          if (!context) {
            throw new Error('Could not get canvas context');
          }

          tempCanvas.width = viewport.width;
          tempCanvas.height = viewport.height;

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          await page.render(renderContext).promise;

          tempCanvas.toBlob(
            (blob) => {
              if (blob) {
                const selectedFileId = `${fileId}_${pageNum}`;
                const pageData = {
                  pageNumber: pageNum,
                  pageBlob: blob,
                  isPortrait,
                  isLandscape,
                  isSquare,
                  selectedFileId,
                  fileId
                };
                setSelectedPage((prev) => {
                  if (prev?.selectedFileId === selectedFileId) return null;
                  return pageData;
                });
              }

              tempCanvas.width = 0;
              tempCanvas.height = 0;
              if (tempCanvas.parentNode) {
                tempCanvas.parentNode.removeChild(tempCanvas);
              }
            },
            'image/png',
            1
          );
        } catch (error) {
          console.error('Error rendering page at original size:', error);
        }
      }
    },
    [setSelectedPage]
  );

  const onDocumentLoadError = useCallback((error: any) => {
    setHasError(true);
    setIsPasswordError(false);
    setIsLoading(false);
    console.error('PDF load error:', error);
  }, []);

  const ErrorComponent = () => (
    <div className="pdf-preview-error-container">
      <div className="pdf-preview-error-message">
        {isPasswordError
          ? 'Failed to load PDF - Password-protected PDFs are not supported'
          : 'Failed to load PDF'}
      </div>
    </div>
  );

  const handlePageCheckboxChange = useCallback(
    (pageNum: number) => (checked: boolean) => {
      const selected = pageNumbers
        ? new Set(pageNumbers)
        : new Set(
            Array.from(
              new Array(pageRef.current.numPages),
              (el, index) => index + 1
            )
          );

      if (checked) {
        selected.add(pageNum);
      } else {
        selected.delete(pageNum);
      }

      if (selected.size === pageRef.current.numPages) {
        onPageSelection([], true);
      } else {
        onPageSelection(
          Array.from(selected).sort((a, b) => a - b),
          false
        );
      }
    },
    [onPageSelection, pageNumbers]
  );

  const renderPages = useMemo(() => {
    if (numPages === 0) return [];
    return Array.from({ length: numPages }, (_, index) => index + 1);
  }, [numPages]);

  return (
    <>
      {hasError ? (
        <ErrorComponent />
      ) : (
        <>
          {isLoading && <SkeletonPlaceholder className="skeleton" />}
          {pdfUrl && (
            <Document
              key={pdfUrl}
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              onPassword={onDocumentPasswordRequest}
              loading={<></>}
            >
              <div
                className="pdf-preview-container"
                style={{
                  height: '166px',
                  justifyContent: 'unset',
                  overflow: 'auto hidden',
                  display: 'flex'
                }}
              >
                {renderPages.map((pageNum) => {
                  const currentFileId = `${fileId}_${pageNum}`;
                  return (
                    <div
                      key={`${fileId}_${pageNum}`}
                      className={`pdf-thumbnail ${
                        selectedFileId === currentFileId ? 'selected' : ''
                      }`}
                    >
                      <Page
                        pageNumber={pageNum}
                        width={70}
                        height={95}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        {...(!isMobileOnly
                          ? { onClick: handlePageClick(pageNum, fileId) }
                          : {})}
                      />
                      <div className="pdf-thumbnail-label">
                        <span>Page {pageNum}</span>
                        {/* if pageNumber have array then search else meaning all checked */}
                        <Checkbox
                          id={`page-checkbox-${fileId}-${pageNum}`}
                          {...(pageNumbers
                            ? { checked: pageNumbers.includes(pageNum) }
                            : { checked: true })}
                          style={{ padding: 0 }}
                          onChange={handlePageCheckboxChange(pageNum)}
                          labelText=""
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Document>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(PDFPreview);
